const MIN_VISIBLE_MS = 700;

/**
 * Discard the locally cached frontend and load the latest deployed build.
 *
 * The dashboard is a PWA: a service worker precaches the build output, so a
 * plain reload keeps serving the old shell until the new worker takes over.
 * For a non-production developer who wants the newest build right now, that
 * wait is the problem. This unregisters every worker for the origin and empties
 * Cache Storage, then reloads — the next load goes to the network and registers
 * a fresh worker against the new build.
 *
 * Only code caches are touched: sign-in (localStorage) and Apollo's data are
 * left alone, so the developer stays logged in on the same tenant.
 * Every step is best-effort; a browser that blocks one still gets the reload.
 */
export async function forceFrontendReload(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch {
    // Unregistering is best-effort — reload anyway.
  }

  try {
    if ('caches' in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch {
    // Cache Storage can be unavailable (blocked storage) — reload anyway.
  }

  // Clearing can finish in a few milliseconds; hold the reload just long enough
  // for the caller's "Reloading…" state to be seen, so the click visibly did something.
  await new Promise((resolve) => window.setTimeout(resolve, MIN_VISIBLE_MS));

  window.location.reload();
}
